import React, { useEffect, useMemo, useState } from "react";
import { Card, Form } from "react-bootstrap";
import { shallowEqual, useSelector } from "react-redux";
import { useSubheader } from "../../../_metronic/layout";
import * as budgetApi from "../../api/budget";
import * as usersApi from "../../api/users";
import { roles } from "../../config/config";
import BudgetSummaryTable from "./components/BudgetSummaryTable";
import EditBudgetModal from "./components/EditBudgetModal";
import ExpenseDetailModal from "./components/ExpenseDetailModal";
import {
  getAvailableYears,
  isBudgetConfigured,
  isExpenseCharged,
  matchesBudgetExpense,
  matchesBudgetRow,
  sortAgentsByLastName,
} from "./BudgetPage.utils";

const getErrorMessage = (error) =>
  error && (error.plainMessage || error.message)
    ? error.plainMessage || error.message
    : "Si è verificato un errore. Riprova.";

const BudgetPage = () => {
  const user = useSelector((state) => state.auth.user, shallowEqual);
  const subheader = useSubheader();
  const currentYear = new Date().getFullYear();
  const managerRoles = [roles.admin, roles.gestore, roles.developer, roles.contabile];
  const canManageBudgets = Boolean(user && managerRoles.indexOf(Number(user.idRole)) !== -1);
  const agentRoles = [roles.agenteRoydermal, roles.agenteRoypet, roles.agenteValderma, roles.agenteCompleto];
  const isBudgetAgent = Boolean(user && agentRoles.indexOf(Number(user.idRole)) !== -1);
  const [year, setYear] = useState(currentYear);
  const [budgetSearch, setBudgetSearch] = useState("");
  const [budgets, setBudgets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editBudget, setEditBudget] = useState(null);
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [expensesFor, setExpensesFor] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [expenseSearch, setExpenseSearch] = useState("");
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [expensesError, setExpensesError] = useState("");

  subheader.setTitle("Budget annuali");

  const loadBudgets = async () => {
    setLoading(true);
    setError("");
    try {
      if (canManageBudgets) {
        const result = await Promise.all([budgetApi.getBudgets(user.authToken), usersApi.getAll(user.authToken)]);
        setBudgets(Array.isArray(result[0]) ? result[0] : []);
        setAgents(Array.isArray(result[1]) ? result[1] : []);
      } else {
        const ownBudgets = await budgetApi.getBudgets(user.authToken);
        setBudgets(Array.isArray(ownBudgets) ? ownBudgets : []);
        setAgents([
          {
            id: user.id,
            firstname: user.firstname,
            lastname: user.lastname,
            fullname: [user.lastname, user.firstname].filter(Boolean).join(" "),
            role: user.idRole,
          },
        ]);
      }
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.authToken) loadBudgets();
  }, [user && user.authToken]);

  const availableYears = useMemo(() => getAvailableYears(budgets, currentYear), [budgets, currentYear]);
  const rows = useMemo(
    () =>
      sortAgentsByLastName(agents.filter((agent) => agentRoles.indexOf(Number(agent.role)) !== -1)).map((agent) => {
        const storedBudget = budgets.find(
          (item) => Number(item.idAgente) === Number(agent.id) && Number(item.anno) === Number(year),
        );
        const priorBudget = budgets.find(
          (item) => Number(item.idAgente) === Number(agent.id) && Number(item.anno) === Number(year) - 1,
        );

        return {
          idAgente: agent.id,
          anno: year,
          importo: storedBudget ? storedBudget.importo : 0,
          spese: storedBudget ? storedBudget.spese : 0,
          mesiSpesa: storedBudget ? storedBudget.mesiSpesa : "",
          prefillAmount: priorBudget ? priorBudget.importo : 0,
          exists: isBudgetConfigured(storedBudget),
        };
      }),
    [agents, budgets, year],
  );
  const agentById = useMemo(() => {
    const map = {};
    agents.forEach((agent) => {
      map[String(agent.id)] = agent;
    });
    return map;
  }, [agents]);
  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        const agent = agentById[String(row.idAgente)];
        return matchesBudgetRow(row, budgetSearch, agent ? agent.fullname : "Agente " + row.idAgente);
      }),
    [rows, agentById, budgetSearch],
  );

  const getAgentName = (id) => {
    const agent = agentById[String(id)];
    return agent ? agent.fullname : "Agente " + id;
  };

  const openEdit = (row) => {
    setEditBudget(row);
    setAmount(String(Number(row.exists ? row.importo || 0 : row.prefillAmount || 0)));
  };

  const saveBudget = async (event) => {
    event.preventDefault();
    if (!canManageBudgets) return;
    const parsedAmount = Number(amount);
    if (!isFinite(parsedAmount) || parsedAmount < 0) {
      setError("Inserire un importo valido, maggiore o uguale a zero.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await budgetApi.setBudget(user.authToken, editBudget.idAgente, year, parsedAmount);
      setEditBudget(null);
      await loadBudgets();
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const showExpenses = async (row) => {
    setExpensesFor(row);
    setExpenses([]);
    setExpenseSearch("");
    setExpensesError("");
    setExpensesLoading(true);
    try {
      const result = await budgetApi.getExpenses(user.authToken, row.idAgente, year);
      const allExpenses = Array.isArray(result) ? result : [];
      setExpenses(
        allExpenses.filter(
          (expense) => Number(expense.idAgente) === Number(row.idAgente) && Number(expense.anno) === Number(year),
        ),
      );
    } catch (loadError) {
      setExpensesError(getErrorMessage(loadError));
    } finally {
      setExpensesLoading(false);
    }
  };

  const filteredExpenses = useMemo(
    () =>
      expenses.filter((expense) => {
        const agent = agentById[String(expense.idAgente)];
        const fallbackAgentName = agent ? agent.fullname : "Agente " + expense.idAgente;
        const agentName = [expense.agenteNome, expense.agenteCognome].filter(Boolean).join(" ") || fallbackAgentName;
        return matchesBudgetExpense(expense, expenseSearch, agentName);
      }),
    [expenses, expenseSearch, agentById],
  );
  // Il totale include sia addebiti attivi sia righe storiche senza ledger.
  const expenseTotal = filteredExpenses.reduce(
    (total, expense) => total + (isExpenseCharged(expense) ? Number(expense.importo || 0) : 0),
    0,
  );

  let roleName = "Agente";
  if (user && Number(user.idRole) === roles.admin) roleName = "Amministratore";
  else if (user && Number(user.idRole) === roles.gestore) roleName = "Gestore";
  else if (user && Number(user.idRole) === roles.developer) roleName = "Sviluppatore";
  else if (user && Number(user.idRole) === roles.contabile) roleName = "Contabile";

  return (
    <>
      <Card className="card-custom gutter-b">
        <Card.Header className="border-0 py-5 d-flex align-items-center justify-content-between flex-wrap">
          <div>
            <h3 className="font-weight-bolder text-dark mb-1">
              {isBudgetAgent ? "Il mio budget" : "Budget per agente"}
            </h3>
            <div className="text-muted">
              {canManageBudgets ? "Accesso riservato a " + roleName : "Consultazione personale in sola lettura"}
            </div>
          </div>
          <Form.Group className="mb-0 d-flex align-items-center">
            <Form.Label className="mb-0 mr-3">Anno</Form.Label>
            <Form.Control
              as="select"
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
              aria-label="Anno budget"
            >
              {availableYears.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Card.Header>
        <Card.Body className="pt-0">
          <BudgetSummaryTable
            loading={loading}
            year={year}
            rows={rows}
            filteredRows={filteredRows}
            error={error}
            search={budgetSearch}
            onSearchChange={(event) => setBudgetSearch(event.target.value)}
            onEdit={openEdit}
            onShowExpenses={showExpenses}
            canManageBudgets={canManageBudgets}
            getAgentName={getAgentName}
          />
        </Card.Body>
      </Card>
      <EditBudgetModal
        budget={editBudget}
        getAgentName={getAgentName}
        amount={amount}
        onAmountChange={(event) => setAmount(event.target.value)}
        onHide={() => setEditBudget(null)}
        onSubmit={saveBudget}
        saving={saving}
      />
      <ExpenseDetailModal
        budget={expensesFor}
        year={year}
        getAgentName={getAgentName}
        search={expenseSearch}
        onSearchChange={(event) => setExpenseSearch(event.target.value)}
        expenses={expenses}
        filteredExpenses={filteredExpenses}
        loading={expensesLoading}
        error={expensesError}
        total={expenseTotal}
        onHide={() => {
          setExpensesFor(null);
          setExpenseSearch("");
        }}
      />
    </>
  );
};

export default BudgetPage;
